import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { event } from '../analytics';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const success = await login(username, password);
      if (success) {
        event('login', { event_category: 'auth', event_action: 'login', method: 'form' });
        navigate('/dashboard');
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: '#f0fdfa',
      }}
    >
      {/* Left Side - Corporate Content */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #134e4a 100%)',
          color: 'white',
          p: 8,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(13, 148, 136, 0.2) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(245, 158, 11, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 600 }}>
          <Typography variant="h2" component="h1" gutterBottom sx={{ 
            fontWeight: 800, 
            mb: 3,
            background: 'linear-gradient(135deg, #5eead4 0%, #2d4a6f 50%, #1e3a5f 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Verit Compliance
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, color: '#99f6e4', fontWeight: 400 }}>
            AI Safety Intelligence Platform
          </Typography>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem', lineHeight: 1.7, color: '#e0f2fe' }}>
              Proactively detect, analyze, and mitigate risks in your AI systems with intelligent monitoring and real-time insights.
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7, color: '#a7f3d0' }}>
              Built for developers, compliance teams, and organizations committed to responsible AI deployment.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                width: 10, 
                height: 10, 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)',
                boxShadow: '0 0 12px rgba(13, 148, 136, 0.6)',
              }} />
              <Typography variant="body1" sx={{ fontSize: '1rem', color: '#e0f2fe' }}>
                Intelligent risk detection across all AI interactions
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                width: 10, 
                height: 10, 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)',
                boxShadow: '0 0 12px rgba(245, 158, 11, 0.5)',
              }} />
              <Typography variant="body1" sx={{ fontSize: '1rem', color: '#e0f2fe' }}>
                Automated alerts and comprehensive audit trails
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                width: 10, 
                height: 10, 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                boxShadow: '0 0 12px rgba(8, 145, 178, 0.5)',
              }} />
              <Typography variant="body1" sx={{ fontSize: '1rem', color: '#e0f2fe' }}>
                Enterprise-grade security and compliance reporting
              </Typography>
            </Box>
          </Box>
        </Box>
        
        {/* Decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(13, 148, 136, 0.12) 0%, transparent 70%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -150,
            left: -150,
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(245, 158, 11, 0.08) 0%, transparent 70%)',
          }}
        />
      </Box>

      {/* Right Side - Login Form */}
      <Box
        sx={{
          flex: { xs: 1, md: 0.5 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          bgcolor: '#ffffff',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 480, px: 3 }}>
          <Box sx={{ mb: 5 }}>
            <Typography variant="h4" component="h2" gutterBottom sx={{ 
              fontWeight: 700, 
              background: 'linear-gradient(135deg, #0f172a 0%, #134e4a 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Sign In
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
              Welcome back. Please enter your credentials to access your account.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                Username
              </Typography>
              <TextField
                fullWidth
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                variant="outlined"
                placeholder="Enter your username"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#1e3a5f',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1e3a5f',
                    },
                  },
                }}
              />
            </Box>
            
            <Box sx={{ mb: 4 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                Password
              </Typography>
              <TextField
                fullWidth
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                variant="outlined"
                placeholder="Enter your password"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#1e3a5f',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1e3a5f',
                    },
                  },
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              data-ga-label="Login submit"
              sx={{
                py: 1.75,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #1e3a5f 0%, #152a4a 100%)',
                textTransform: 'none',
                boxShadow: '0 4px 16px rgba(13, 148, 136, 0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2d4a6f 0%, #1e3a5f 100%)',
                  boxShadow: '0 6px 24px rgba(13, 148, 136, 0.45)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  background: 'linear-gradient(135deg, #5eead4 0%, #2d4a6f 100%)',
                  boxShadow: 'none',
                },
              }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </Box>

          <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Don't have an account?{' '}
              <Typography
                component="a"
                href="mailto:sales@veritcompliance.com"
                data-ga-label="Contact sales email"
                sx={{ 
                  background: 'linear-gradient(90deg, #1e3a5f 0%, #0ea5e9 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}>
                Contact our sales team
              </Typography>{' '}
              to get started with Verit Compliance.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
